import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./components/ui/popover";
import { Button } from "./components/ui/button";
import { PayPalSupport } from "./PayPalSupport";
import { Copy, ExternalLink, Instagram, Send, Share2 } from "lucide-react";

/** Simple SVG marks (kept inline so we don't pull in an icon lib for two brands). */
const WhatsAppIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 2.1.55 4.16 1.6 5.98L2 22l4.25-1.11a9.9 9.9 0 0 0 5.78 1.84h.01c5.46 0 9.9-4.45 9.9-9.91S17.5 2 12.04 2Zm0 18.13h-.01a8.2 8.2 0 0 1-4.17-1.14l-.3-.18-2.52.66.67-2.46-.2-.31A8.24 8.24 0 0 1 3.8 11.9c0-4.55 3.7-8.25 8.24-8.25 2.2 0 4.27.86 5.83 2.42a8.2 8.2 0 0 1 2.41 5.83c0 4.55-3.69 8.24-8.24 8.24Zm4.52-6.17c-.25-.13-1.46-.72-1.69-.8-.22-.09-.39-.13-.55.13-.16.25-.63.8-.77.96-.14.16-.28.18-.53.06-.25-.13-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.71-.14-.25-.02-.39.11-.51.11-.11.25-.28.38-.42.13-.14.16-.25.25-.41.09-.16.05-.31-.02-.44-.06-.13-.55-1.34-.76-1.83-.2-.48-.4-.42-.55-.42h-.47c-.16 0-.42.06-.63.31-.22.25-.83.81-.83 1.98s.85 2.29.97 2.45c.13.16 1.68 2.57 4.07 3.6.57.24 1.01.39 1.36.5.57.18 1.09.15 1.5.09.46-.07 1.46-.6 1.66-1.18.2-.58.2-1.07.14-1.18-.06-.11-.22-.17-.47-.3Z" />
  </svg>
);

const TelegramIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M9.78 15.4 9.6 18.8c.25 0 .36-.11.5-.24l1.2-1.14 2.49 1.82c.46.25.79.12.9-.42l1.63-7.66h.01c.14-.66-.24-.92-.68-.75L5.74 13.83c-.65.25-.64.62-.11.79l2.5.78 5.81-3.66c.27-.19.52-.09.32.1l-4.48 4.06Z" />
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Z" fill="none" />
  </svg>
);

const IG_URL = "https://www.instagram.com/inthegreenlane/";
const PAGE_BLURB = "In The Green Lane — a live index of the birds featured on @inthegreenlane on Instagram, with rare-sighting alerts and a monthly bird map for Singapore.";
const IG_BLURB = "Follow @inthegreenlane on Instagram — beautiful photography of Singapore's wild birds.";

const buildLinks = ({ url, text }) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(`${text} ${url}`);
  const encodedTextOnly = encodeURIComponent(text);
  return {
    whatsapp: `https://api.whatsapp.com/send?text=${encodedText}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTextOnly}`,
  };
};

const openWindow = (href) => {
  window.open(href, "_blank", "noopener,noreferrer");
};

const ShareMenu = () => {
  const [open, setOpen] = useState(false);

  const pageUrl = useMemo(
    () => (typeof window !== "undefined" ? window.location.origin : ""),
    []
  );

  const pageLinks = useMemo(
    () => buildLinks({ url: pageUrl, text: PAGE_BLURB }),
    [pageUrl]
  );
  const igLinks = useMemo(
    () => buildLinks({ url: IG_URL, text: IG_BLURB }),
    []
  );

  const copyLink = useCallback(async (url, label) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`${label} link copied`);
    } catch {
      toast.error("Could not copy — please copy from the URL bar");
    }
  }, []);

  const nativeShare = useCallback(
    async ({ url, text, label }) => {
      if (!navigator.share) return false;
      try {
        await navigator.share({
          title: "In The Green Lane",
          text,
          url,
        });
        setOpen(false);
        return true;
      } catch {
        // user cancelled or share unsupported — silently swallow
        return true;
      }
    },
    []
  );

  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid="share-button"
          variant="outline"
          className="border-[#E5E2DA] text-[#5C5C5C] hover:bg-[#F3F0E7] hover:text-[#2C5530] hover:border-[#2C5530] rounded-full px-3 sm:px-4"
          title="Share"
        >
          <Share2 size={14} className="sm:mr-2" />
          <span className="hidden sm:inline">Share</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        data-testid="share-menu"
        className="w-80 max-h-[85vh] overflow-y-auto p-0 border-[#E5E2DA] bg-[#FAF9F6] shadow-lg"
      >
        <div className="p-5 border-b border-[#E5E2DA]">
          <div className="label-eyebrow mb-1 text-[#8C7A6B]">SHARE</div>
          <div className="font-serif-display text-lg text-[#1A1A1A] leading-tight">
            Send a little bird
            <br />
            <span className="italic">someone's way</span>
          </div>
        </div>

        <ShareGroup
          title="THIS FIELD GUIDE"
          hint="in-the-green-lane app"
          testidPrefix="share-app"
          links={pageLinks}
          onCopy={() => copyLink(pageUrl, "Page")}
          onNative={
            canNativeShare
              ? () =>
                  nativeShare({
                    url: pageUrl,
                    text: PAGE_BLURB,
                    label: "This field guide",
                  })
              : null
          }
        />

        <div className="h-px bg-[#E5E2DA]" />

        <ShareGroup
          title="@INTHEGREENLANE ON INSTAGRAM"
          hint={<span className="inline-flex items-center gap-1">Follow the source <ExternalLink size={11} /></span>}
          testidPrefix="share-ig"
          links={igLinks}
          onCopy={() => copyLink(IG_URL, "Instagram")}
          onNative={
            canNativeShare
              ? () =>
                  nativeShare({
                    url: IG_URL,
                    text: IG_BLURB,
                    label: "Instagram",
                  })
              : null
          }
          igLink={IG_URL}
        />
        <PayPalSupport />
      </PopoverContent>
    </Popover>
  );
};

const ShareGroup = ({ title, hint, links, testidPrefix, onCopy, onNative, igLink }) => (
  <div className="p-5 space-y-3">
    <div className="flex items-baseline justify-between gap-3">
      <div className="label-eyebrow leading-tight">{title}</div>
      {igLink ? (
        <a
          href={igLink}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-[10px] tracking-widest uppercase text-[#8C7A6B] hover:text-[#2C5530] inline-flex items-center gap-1"
        >
          {hint}
        </a>
      ) : (
        <div className="font-mono text-[10px] tracking-widest uppercase text-[#8C7A6B]">
          {hint}
        </div>
      )}
    </div>
    <div className="grid grid-cols-2 gap-2">
      <ShareButton
        testid={`${testidPrefix}-whatsapp`}
        icon={<WhatsAppIcon />}
        label="WhatsApp"
        color="#25D366"
        onClick={() => openWindow(links.whatsapp)}
      />
      <ShareButton
        testid={`${testidPrefix}-telegram`}
        icon={<Send size={14} />}
        label="Telegram"
        color="#229ED9"
        onClick={() => openWindow(links.telegram)}
      />
    </div>
    <div className="grid grid-cols-2 gap-2">
      <ShareButton
        testid={`${testidPrefix}-copy`}
        icon={<Copy size={14} />}
        label="Copy link"
        color="#5C5C5C"
        variant="ghost"
        onClick={onCopy}
      />
      {onNative ? (
        <ShareButton
          testid={`${testidPrefix}-native`}
          icon={<Share2 size={14} />}
          label="More…"
          color="#5C5C5C"
          variant="ghost"
          onClick={onNative}
        />
      ) : testidPrefix === "share-ig" ? (
        <a
          data-testid={`${testidPrefix}-open`}
          href={igLink || IG_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 h-9 rounded-full text-xs font-medium text-[#5C5C5C] hover:bg-[#F3F0E7] border border-[#E5E2DA]"
        >
          <Instagram size={14} /> Open profile
        </a>
      ) : (
        <div />
      )}
    </div>
  </div>
);

const ShareButton = ({ testid, icon, label, color, variant, onClick }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={
      variant === "ghost"
        ? "inline-flex items-center justify-center gap-2 h-9 rounded-full text-xs font-medium text-[#5C5C5C] hover:bg-[#F3F0E7] border border-[#E5E2DA] transition-colors"
        : "inline-flex items-center justify-center gap-2 h-9 rounded-full text-xs font-medium text-white transition-transform hover:scale-[1.02] active:scale-[0.98]"
    }
    style={variant === "ghost" ? undefined : { backgroundColor: color }}
  >
    <span className="inline-flex items-center">{icon}</span>
    {label}
  </button>
);

export default ShareMenu;
